import { useMemo, useState } from 'react';
import { PageHeader } from '@/components/common/PageHeader';
import { useToast } from '@/components/common/toast';
import { FilterBar } from '@/components/common/FilterBar';
import { AsyncButton } from '@/components/common/AsyncButton';
import { Select2 } from '@/components/forms/Select2';
import { useServerDataTable } from '@/components/tables/useServerDataTable';
import { DataGrid, type DataGridColumn } from '@/components/tables/DataGrid';
import { http } from '@/api/client/httpClient';
import { latestUniqueIdApi, type LatestUniqueIdInput, type LatestUniqueIdRow } from '@/api/services/customReports';

const QUARTER_OPTIONS = [
  { value: 1, label: 'Quarter 1' },
  { value: 2, label: 'Quarter 2' },
  { value: 3, label: 'Quarter 3' },
  { value: 4, label: 'Quarter 4' },
];

function currentYear() {
  return new Date().getFullYear();
}
function currentQuarter() {
  return Math.floor(new Date().getMonth() / 3) + 1;
}

interface Period {
  year: number;
  quarter: number;
}

export function LatestUniqueIdPage() {
  const toast = useToast();
  const [draft, setDraft] = useState<Period>({ year: currentYear(), quarter: currentQuarter() });
  const [applied, setApplied] = useState<Period>(draft);

  const extra = useMemo<LatestUniqueIdInput>(() => ({ year: applied.year, quarter: applied.quarter }), [applied]);
  const table = useServerDataTable<LatestUniqueIdRow, LatestUniqueIdInput>({
    queryKey: ['latest-unique-id', 'rows'],
    fetcher: (req) => latestUniqueIdApi.getRows(req),
    pageSize: 5,
    extraParams: extra,
  });

  const fetch = () => {
    table.setPageIndex(0);
    setApplied({ ...draft });
  };

  async function extract() {
    if (!draft.year || !draft.quarter) {
      toast.warning('Please select Year and Quarter.');
      return;
    }
    try {
      const res = await http.get(latestUniqueIdApi.excelUrl, { params: { year: draft.year, quarter: draft.quarter }, responseType: 'blob' });
      const url = URL.createObjectURL(res.data as Blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Latest Unique Id.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to extract the report.');
    }
  }

  const fields: React.ReactNode[] = [
    <Select2
      key="period"
      label="Period"
      searchable={false}
      allowClear={false}
      value={draft.quarter}
      options={QUARTER_OPTIONS}
      onChange={(v) => setDraft((f) => ({ ...f, quarter: v ? Number(v) : currentQuarter() }))}
    />,
    <div key="year">
      <label className="form-label">Year</label>
      <input className="form-control" inputMode="numeric" value={draft.year ?? ''} onChange={(e) => setDraft((f) => ({ ...f, year: e.target.value ? Number(e.target.value) : 0 }))} />
    </div>,
  ];

  return (
    <div className="latest-unique-id-page">
      <PageHeader title="Latest Unique Id" breadcrumb={['Home', 'Custom Reports', 'Latest Unique Id']} />

      <FilterBar
        fields={fields}
        actions={
          <>
            <AsyncButton className="btn btn-primary btn-sm" icon="fa fa-search" loading={table.isFetching} onClick={fetch}>Fetch</AsyncButton>
            <AsyncButton className="btn btn-outline-primary btn-sm" icon="fa fa-download" onClick={extract}>Extract</AsyncButton>
          </>
        }
      />

      <DataGrid
        columns={[
          { key: 'uniqueId', title: 'Unique Id', className: 'text-start', value: (r) => r.uniqueId },
          { key: 'country', title: 'Country', className: 'text-start', value: (r) => r.country },
          { key: 'miFIDClassification', title: 'MIFID Classification', className: 'text-start', value: (r) => r.miFIDClassification },
        ] as DataGridColumn<LatestUniqueIdRow>[]}
        data={table.rows}
        rowKey={(r) => `${r.uniqueId}-${r.country}-${r.miFIDClassification}`}
        card
        columnSearch
        columnSearchValues={table.columnFilters}
        onColumnSearchChange={(key, value) => table.setColumnFilter(key, value)}
        emptyMessage={table.isLoading ? 'Loading…' : 'No data.'}
        server={{ pageIndex: table.pageIndex, pageSize: table.pageSize, pageCount: table.pageCount, totalCount: table.totalCount, onPageChange: table.setPageIndex, onPageSizeChange: table.setPageSize }}
      />
    </div>
  );
}
